import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../utills/api";

import {
  Container,
  Row,
  Col,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import qz from "qz-tray";

import MenuHeader from "./Component/MenuHeader";
import MenuItems from "./Component/MenuItems";
import BillingSection from "./Component/BillingSection";

const CreateOrder = () => {
  document.title = "Restaurant POS";

  /* ======================================
     STATE
  ====================================== */

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [orderType, setOrderType] =
    useState("DINE_IN");

  const [rooms, setRooms] =
    useState([]);

  const [selectedRoomType, setSelectedRoomType] =
    useState("");

  const [selectedRoom, setSelectedRoom] =
    useState("");

  const [propertyProfile, setPropertyProfile] =
    useState(null);

  const [selectedCategory, setSelectedCategory] =
    useState("ALL");

  const [foodType, setFoodType] =
    useState("Veg");

  const [search, setSearch] =
    useState("");

  const [cart, setCart] =
    useState([]);

  const [selectedSizes, setSelectedSizes] =
    useState({});

  const [generatedKOT, setGeneratedKOT] =
    useState(null);


  /* ======================================
     FETCH DATA
  ====================================== */

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchRooms();
    loadPropertyProfile();
  }, []);


  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        "/menu/menu-settings"
      );

      setCategories(res.data || []);

    } catch (err) {
      console.log(
        "CATEGORY ERROR:",
        err
      );
    }
  };


  const fetchProducts = async () => {
    try {
      const res = await axios.get(
        "/products"
      );

      setProducts(res.data || []);

    } catch (err) {
      console.log(
        "PRODUCT ERROR:",
        err
      );
    }
  };


  const fetchRooms = async () => {
    try {
      const res = await axios.get(
        "/manage/add-rooms"
      );

      setRooms(res.data || []);

    } catch (err) {
      console.log(
        "ROOM ERROR:",
        err
      );
    }
  };


  const loadPropertyProfile = async () => {
    try {
      const res = await axios.get(
        "/property-profile"
      );

      setPropertyProfile(
        res.data.profile
      );

    } catch (error) {
      console.error(
        "Failed to load property profile",
        error
      );
    }
  };


  /* ======================================
     FILTER PRODUCTS
  ====================================== */

  const filteredProducts = useMemo(() => {

    let data = [...products];

    /* CATEGORY */

    if (
      selectedCategory !== "ALL"
    ) {
      data = data.filter(
        (item) =>
          item.category ===
          selectedCategory
      );
    }


    /* FOOD TYPE */

    data = data.filter(
      (item) =>
        item.type?.toLowerCase() ===
        foodType.toLowerCase()
    );


    /* SEARCH */

    if (search) {

      const searchText =
        search.toLowerCase();

      data = data.filter(
        (item) => {

          const english =
            item.name?.en
              ?.toLowerCase() || "";

          const hindi =
            item.name?.hi
              ?.toLowerCase() || "";

          return (
            english.includes(
              searchText
            ) ||
            hindi.includes(
              searchText
            )
          );
        }
      );
    }

    return data;

  }, [
    products,
    selectedCategory,
    foodType,
    search,
  ]);


  /* ======================================
     CART
  ====================================== */

  const addToCart = (
    product,
    size = "FULL"
  ) => {

    const existing =
      cart.find(
        (item) =>
          item._id === product._id &&
          item.size === size
      );


    const finalPrice =
      size === "HALF"
        ? product.price / 2
        : product.price;


    if (existing) {

      setCart((prev) =>
        prev.map((item) =>
          item._id === product._id &&
          item.size === size

            ? {
                ...item,
                quantity:
                  item.quantity + 1,
              }

            : item
        )
      );

    } else {

      setCart((prev) => [

        ...prev,

        {
          ...product,
          size,
          quantity: 1,
          finalPrice,
        },

      ]);

    }

  };


  const updateQty = (
    index,
    type
  ) => {

    const updated =
      [...cart];


    if (
      type === "INC"
    ) {

      updated[index].quantity += 1;

    }


    if (
      type === "DEC"
    ) {

      updated[index].quantity -= 1;


      if (
        updated[index].quantity <= 0
      ) {

        updated.splice(
          index,
          1
        );

      }

    }


    setCart(updated);

  };


  /* ======================================
     TOTALS
  ====================================== */

  const subTotal =
    cart.reduce(
      (total, item) =>
        total +
        (
          item.finalPrice *
          item.quantity
        ),
      0
    );


  const gstAmount =
    subTotal * 0.05;


  const grandTotal =
    subTotal +
    gstAmount;


  /* ======================================
     GENERATE KOT
  ====================================== */

  const generateKOT = async () => {

    try {

      if (
        orderType === "ROOM_DELIVERY" &&
        (
          !selectedRoom ||
          selectedRoom === "Others"
        )
      ) {

        alert(
          "Please select a valid room"
        );

        return;

      }


      if (
        !cart.length
      ) {

        alert(
          "Please select at least one item"
        );

        return;

      }


      const payload = {

        orderType,

        roomType:
          selectedRoom
            ? selectedRoomType
            : null,

        roomNumber:
          selectedRoom || null,


        items:

          cart.map(
            (item) => ({

              productId:
                item._id,

              name: {

                en:
                  typeof item.name ===
                  "string"
                    ? item.name
                    : item.name?.en || "",

                hi:
                  typeof item.name ===
                  "object"
                    ? item.name?.hi || ""
                    : "",

              },


              type:
                item.size || "FULL",


              quantity:
                Number(
                  item.quantity
                ),


              price:
                Number(
                  item.finalPrice
                ),


              total:

                Number(
                  item.finalPrice *
                  item.quantity
                ),

            })
          ),


        subTotal,

        gst:
          gstAmount,

        grandTotal,

      };


      console.log(
        "GENERATING KOT:",
        payload
      );


      const res =
        await axios.post(
          "/pos/generate-kot",
          payload
        );


      alert(
        "KOT Generated Successfully"
      );


      setGeneratedKOT(
        res.data.kot
      );


      /* PRINT */

      printBill(
        res.data.kot
      );


      printKOTThermal(
        res.data.kot
      );


      /* CLEAR CART */

      setCart([]);

    } catch (err) {

      console.log(
        "GENERATE KOT ERROR:",
        err
      );


      alert(

        err.response?.data?.message ||

        "Failed to generate KOT"

      );

    }

  };


  /* ======================================
     PRINT BILL
  ====================================== */

  const printBill = (
    kotData
  ) => {

    const printWindow =
      window.open(
        "",
        "",
        "width=400,height=600"
      );


    const itemsHtml =
      kotData.items
        .map(
          (item) => `
          
            <tr>
            
              <td>
                ${item.name?.en || ""}
                
                ${
                  item.name?.hi
                    ? `<br/><small>${item.name.hi}</small>`
                    : ""
                }
                
              </td>

              <td>
                ${item.type}
              </td>

              <td>
                ${item.quantity}
              </td>

              <td>
                ₹${item.total}
              </td>

            </tr>

          `
        )
        .join("");


    printWindow.document.write(`

      <html>

        <head>

          <title>
            KOT BILL
          </title>

          <style>

            body {
              font-family: monospace;
              padding: 10px;
              font-size: 12px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            td {
              padding: 4px;
            }

            .center {
              text-align: center;
            }

            hr {
              border-top: 1px dashed #000;
            }

          </style>

        </head>


        <body>

          <div class="center">

            <h2>
              ${
                propertyProfile
                  ?.legalBusinessName ||
                "PROPERTY"
              }
            </h2>

            <div>
              GSTIN:
              ${
                propertyProfile
                  ?.gstNumber ||
                "N/A"
              }
            </div>

            <div>
              PAN:
              ${
                propertyProfile
                  ?.panNumber ||
                "N/A"
              }
            </div>

            <h4>
              KOT BILL
            </h4>

          </div>


          <hr />


          <p>
            <strong>KOT NO:</strong>
            ${kotData.kotNumber}
          </p>


          <p>
            <strong>ROOM:</strong>
            ${
              kotData.roomNumber ||
              "-"
            }
          </p>


          <hr />


          <table>

            <thead>

              <tr>

                <td>
                  <b>Item</b>
                </td>

                <td>
                  <b>Type</b>
                </td>

                <td>
                  <b>Qty</b>
                </td>

                <td>
                  <b>Total</b>
                </td>

              </tr>

            </thead>


            <tbody>

              ${itemsHtml}

            </tbody>

          </table>


          <hr />


          <p>
            Subtotal:
            ₹${Number(
              kotData.subTotal || 0
            ).toFixed(2)}
          </p>


          <p>
            GST:
            ₹${Number(
              kotData.gst || 0
            ).toFixed(2)}
          </p>


          <h3>
            Grand Total:
            ₹${Number(
              kotData.grandTotal || 0
            ).toFixed(2)}
          </h3>


          <hr />


          <div class="center">

            Thank You For Visiting

          </div>

        </body>

      </html>

    `);


    printWindow.document.close();


    setTimeout(() => {

      printWindow.print();

    }, 500);

  };


  /* ======================================
     QZ CONNECT
  ====================================== */

  const connectQZ = async () => {

    try {

      if (
        !qz.websocket.isActive()
      ) {

        await qz.websocket.connect();

      }

      return true;

    } catch (err) {

      console.log(err);

      alert(
        "QZ Tray not running"
      );

      return false;

    }

  };


  /* ======================================
     THERMAL PRINT
  ====================================== */

  const printKOTThermal =
    async (kotData) => {

      try {

        const connected =
          await connectQZ();


        if (!connected) {
          return;
        }


        const printerName =
          "XP-80C";


        const config =
          qz.configs.create(
            printerName
          );


        let data = "";


        data +=
          "\x1B\x40";


        data +=
          "\x1B\x61\x01";


        data +=
          "YATRA NEST\n";

        data +=
          "RESTAURANT KOT\n";

        data +=
          "------------------------------\n";


        data +=
          "\x1B\x61\x00";


        data +=
          `KOT NO : ${kotData.kotNumber}\n`;


        data +=
          `ROOM : ${
            kotData.roomNumber || "-"
          }\n`;


        data +=
          "------------------------------\n";


        kotData.items.forEach(
          (item) => {

            data +=
              `${item.name?.en || ""}\n`;

            data +=
              `${item.type} x${item.quantity}`;

            data +=
              `   ₹${item.total}\n`;

            data +=
              "------------------------------\n";

          }
        );


        data +=
          `SUBTOTAL : ₹${kotData.subTotal}\n`;

        data +=
          `GST : ₹${kotData.gst}\n`;

        data +=
          `TOTAL : ₹${kotData.grandTotal}\n`;


        data +=
          "\nTHANK YOU\n\n\n";


        data +=
          "\x1D\x56\x41";


        await qz.print(
          config,
          [

            {
              type: "raw",
              format: "plain",
              data,
            },

          ]
        );

      } catch (err) {

        console.log(
          "PRINT ERROR:",
          err
        );

      }

    };


  /* ======================================
     UI
  ====================================== */

  return (

    <div className="page-content">

      <Container fluid>

        <BreadCrumb
          title="Restaurant POS"
          pageTitle="Restaurant"
        />


        <Row>


          {/* LEFT SIDE */}

          <Col md={9}>

            <MenuHeader

              search={search}
              setSearch={setSearch}

              orderType={orderType}
              setOrderType={setOrderType}

              selectedRoom={selectedRoom}
              setSelectedRoom={setSelectedRoom}

              rooms={rooms}

              foodType={foodType}
              setFoodType={setFoodType}

              categories={categories}

              selectedCategory={
                selectedCategory
              }

              setSelectedCategory={
                setSelectedCategory
              }

            />


            <MenuItems

              products={
                filteredProducts
              }

              cart={cart}

              selectedSizes={
                selectedSizes
              }

              setSelectedSizes={
                setSelectedSizes
              }

              addToCart={
                addToCart
              }

              updateQty={
                updateQty
              }

            />

          </Col>


          {/* RIGHT SIDE */}

          <Col md={3}>

            <BillingSection

              propertyProfile={
                propertyProfile
              }

              cart={cart}

              subTotal={
                subTotal
              }

              gstAmount={
                gstAmount
              }

              grandTotal={
                grandTotal
              }

              generateKOT={
                generateKOT
              }

            />

          </Col>

        </Row>

      </Container>

    </div>

  );

};

export default CreateOrder;